import { type ButtonHTMLAttributes } from "react";
import { cn } from "@/lib/utils";

export function Button({
  className,
  variant = "primary",
  size = "default",
  ...props
}: ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: "primary" | "secondary" | "ghost" | "danger";
  size?: "default" | "sm";
}) {
  return (
    <button
      className={cn(
        "inline-flex items-center justify-center rounded-lg font-semibold transition-all disabled:opacity-50 disabled:pointer-events-none",
        size === "default" && "px-4 py-2.5 text-sm",
        size === "sm" && "px-3 py-1.5 text-xs",
        variant === "primary" &&
          "bg-argon-primary text-white shadow-[0_4px_6px_rgba(50,50,93,0.11),0_1px_3px_rgba(0,0,0,0.08)] hover:bg-argon-primary-dark",
        variant === "secondary" &&
          "bg-white border border-gray-200 text-argon-dark shadow-sm hover:bg-gray-50",
        variant === "ghost" && "text-argon-secondary hover:bg-gray-100 hover:text-argon-dark",
        variant === "danger" && "bg-argon-danger text-white hover:bg-[#ec0c38]",
        className,
      )}
      {...props}
    />
  );
}

export function Input({ className, ...props }: React.InputHTMLAttributes<HTMLInputElement>) {
  return (
    <input
      className={cn(
        "flex h-10 w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-argon-dark placeholder:text-argon-secondary/70 shadow-sm transition-colors focus:border-argon-primary focus:outline-none focus:ring-2 focus:ring-argon-primary/20",
        className,
      )}
      {...props}
    />
  );
}

export function Select({ className, ...props }: React.SelectHTMLAttributes<HTMLSelectElement>) {
  return (
    <select
      className={cn(
        "flex h-10 w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-argon-dark shadow-sm transition-colors focus:border-argon-primary focus:outline-none focus:ring-2 focus:ring-argon-primary/20",
        className,
      )}
      {...props}
    />
  );
}

export function Textarea({ className, ...props }: React.TextareaHTMLAttributes<HTMLTextAreaElement>) {
  return (
    <textarea
      className={cn(
        "flex min-h-[80px] w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-argon-dark shadow-sm transition-colors focus:border-argon-primary focus:outline-none focus:ring-2 focus:ring-argon-primary/20",
        className,
      )}
      {...props}
    />
  );
}

export function Label({ className, ...props }: React.LabelHTMLAttributes<HTMLLabelElement>) {
  return <label className={cn("text-xs font-bold uppercase tracking-wide text-argon-secondary", className)} {...props} />;
}
